import { getSessionAuthentication, baseUrl } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface DeliveryTerm {
    id: number;
    code: string;
    name: string;
    leadTimeInDays?: number;
    localizations: Localization[];
}

export async function getDeliveryTerm(id: number): Promise<DeliveryTerm> {
    return legacyFetch<DeliveryTerm>(`deliveryterms/${id}`);
}

export async function createDeliveryTerm(dt: Partial<DeliveryTerm>): Promise<DeliveryTerm> {
    return legacyFetch<DeliveryTerm>('deliveryterms', {
        method: 'POST',
        body: JSON.stringify(dt),
    });
}

export async function updateDeliveryTerm(dt: DeliveryTerm): Promise<DeliveryTerm> {
    return legacyFetch<DeliveryTerm>(`deliveryterms/${dt.id}`, {
        method: 'PUT',
        body: JSON.stringify(dt),
    });
}

export async function deleteDeliveryTerm(id: number): Promise<void> {
    await legacyFetch<void>(`deliveryterms/${id}`, { method: 'DELETE' });
}

export async function getLanguages(): Promise<Language[]> {
    return legacyFetch<Language[]>('deliveryterms/languages');
}
